import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const SecuritySection = () => {
  const [activeTab, setActiveTab] = useState('password');
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(true);
  const [showQRCode, setShowQRCode] = useState(false);
  const [backupCodes] = useState([
    'A1B2-C3D4-E5F6',
    'G7H8-I9J0-K1L2',
    'M3N4-O5P6-Q7R8',
    'S9T0-U1V2-W3X4',
    'Y5Z6-A7B8-C9D0'
  ]);
  const [errors, setErrors] = useState({});

  const activeSessions = [
    {
      id: 1,
      device: "Windows Desktop",
      location: "Washington, DC",
      ipAddress: "192.168.1.100",
      lastActive: "2025-01-19 11:15:00",
      current: true
    },
    {
      id: 2,
      device: "iPhone 15 Pro",
      location: "Washington, DC",
      ipAddress: "192.168.1.101",
      lastActive: "2025-01-19 09:30:00",
      current: false
    },
    {
      id: 3,
      device: "MacBook Pro",
      location: "Arlington, VA",
      ipAddress: "10.0.0.50",
      lastActive: "2025-01-18 16:45:00",
      current: false
    }
  ];

  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validatePassword = () => {
    const newErrors = {};
    
    if (!passwordData?.currentPassword) {
      newErrors.currentPassword = 'Current password is required';
    }
    
    if (!passwordData?.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (passwordData?.newPassword?.length < 12) {
      newErrors.newPassword = 'Password must be at least 12 characters long';
    }
    
    if (passwordData?.newPassword !== passwordData?.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handlePasswordSubmit = () => {
    if (validatePassword()) {
      console.log('Changing password...');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    }
  };

  const handleLogoutSession = (sessionId) => {
    console.log('Logging out session:', sessionId);
  };

  const getPasswordStrength = (password) => {
    if (!password) return { strength: 0, label: '' };
    
    let strength = 0;
    if (password?.length >= 8) strength += 1;
    if (password?.length >= 12) strength += 1;
    if (/[A-Z]/?.test(password)) strength += 1;
    if (/[a-z]/?.test(password)) strength += 1;
    if (/[0-9]/?.test(password)) strength += 1;
    if (/[^A-Za-z0-9]/?.test(password)) strength += 1;

    const labels = ['', 'Very Weak', 'Weak', 'Fair', 'Good', 'Strong', 'Very Strong'];
    const colors = ['', 'text-error', 'text-error', 'text-warning', 'text-accent', 'text-success', 'text-success'];
    
    return { 
      strength, 
      label: labels?.[strength] || '',
      color: colors?.[strength] || ''
    };
  };

  const passwordStrength = getPasswordStrength(passwordData?.newPassword);

  const securityTabs = [
    { id: 'password', label: 'Password', icon: 'Lock' },
    { id: '2fa', label: 'Two-Factor Auth', icon: 'Shield' },
    { id: 'sessions', label: 'Active Sessions', icon: 'Monitor' }
  ];

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-12 h-12 bg-error rounded-full flex items-center justify-center">
          <Icon name="Shield" size={24} color="white" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-foreground">Security Settings</h3>
          <p className="text-sm text-muted-foreground">Manage your account security and authentication</p>
        </div>
      </div>
      {/* Security Tabs */}
      <div className="flex space-x-1 mb-6 bg-muted p-1 rounded-lg">
        {securityTabs?.map((tab) => (
          <button
            key={tab?.id}
            onClick={() => setActiveTab(tab?.id)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
              activeTab === tab?.id
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name={tab?.icon} size={16} />
            <span>{tab?.label}</span>
          </button>
        ))}
      </div>
      {/* Password Tab */}
      {activeTab === 'password' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-6">
            <Input
              label="Current Password"
              type="password"
              value={passwordData?.currentPassword}
              onChange={(e) => handlePasswordChange('currentPassword', e?.target?.value)}
              error={errors?.currentPassword}
              required
            />

            <div>
              <Input
                label="New Password"
                type="password"
                value={passwordData?.newPassword}
                onChange={(e) => handlePasswordChange('newPassword', e?.target?.value)}
                error={errors?.newPassword}
                required
                description="Must be at least 12 characters with uppercase, lowercase, numbers, and symbols"
              />
              {passwordData?.newPassword && (
                <div className="mt-2">
                  <div className="flex items-center space-x-2">
                    <div className="flex-1 bg-muted rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full transition-all duration-300 ${
                          passwordStrength?.strength <= 2 ? 'bg-error' :
                          passwordStrength?.strength <= 4 ? 'bg-warning' : 'bg-success'
                        }`}
                        style={{ width: `${(passwordStrength?.strength / 6) * 100}%` }}
                      />
                    </div>
                    <span className={`text-xs font-medium ${passwordStrength?.color}`}>
                      {passwordStrength?.label}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <Input
              label="Confirm New Password"
              type="password"
              value={passwordData?.confirmPassword}
              onChange={(e) => handlePasswordChange('confirmPassword', e?.target?.value)}
              error={errors?.confirmPassword}
              required
            />
          </div>

          <div className="flex justify-end">
            <Button
              variant="default"
              iconName="Save"
              iconPosition="left"
              onClick={handlePasswordSubmit}
            >
              Update Password
            </Button>
          </div>
        </div>
      )}
      {/* Two-Factor Authentication Tab */}
      {activeTab === '2fa' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
            <div className="flex items-center space-x-3">
              <div className={`w-3 h-3 rounded-full ${twoFactorEnabled ? 'bg-success' : 'bg-error'}`} />
              <div>
                <p className="font-medium text-foreground">Two-Factor Authentication</p>
                <p className="text-sm text-muted-foreground">
                  {twoFactorEnabled ? 'Enabled and protecting your account' : 'Disabled - your account is at risk'}
                </p>
              </div>
            </div>
            <Button
              variant={twoFactorEnabled ? "destructive" : "default"}
              onClick={() => setTwoFactorEnabled(!twoFactorEnabled)}
            >
              {twoFactorEnabled ? 'Disable' : 'Enable'}
            </Button>
          </div>

          {twoFactorEnabled && (
            <>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-foreground">Authenticator App</h4>
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="QrCode"
                    iconPosition="left"
                    onClick={() => setShowQRCode(!showQRCode)}
                  >
                    {showQRCode ? 'Hide' : 'Show'} QR Code
                  </Button>
                </div>

                {showQRCode && (
                  <div className="flex flex-col items-center space-y-4 p-6 bg-background rounded-lg border border-border">
                    <div className="w-48 h-48 bg-muted rounded-lg flex items-center justify-center">
                      <Icon name="QrCode" size={64} className="text-muted-foreground" />
                    </div>
                    <p className="text-sm text-muted-foreground text-center">
                      Scan this QR code with your authenticator app
                    </p>
                    <p className="text-xs text-muted-foreground font-mono bg-muted px-3 py-2 rounded">
                      Manual entry: JBSWY3DPEHPK3PXP
                    </p>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <h4 className="font-medium text-foreground">Backup Codes</h4>
                <p className="text-sm text-muted-foreground">
                  Save these backup codes in a secure location. Each code can only be used once.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-4 bg-background rounded-lg border border-border">
                  {backupCodes?.map((code, index) => (
                    <div key={index} className="font-mono text-sm text-foreground bg-muted px-3 py-2 rounded">
                      {code}
                    </div>
                  ))}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="RefreshCw"
                  iconPosition="left"
                >
                  Generate New Codes
                </Button>
              </div>
            </>
          )}
        </div>
      )}
      {/* Active Sessions Tab */}
      {activeTab === 'sessions' && (
        <div className="space-y-6">
          <div className="space-y-4">
            <h4 className="font-medium text-foreground">Active Sessions</h4>
            <p className="text-sm text-muted-foreground">
              Monitor and manage your active login sessions across different devices.
            </p>
          </div>

          <div className="space-y-4">
            {activeSessions?.map((session) => (
              <div key={session?.id} className="flex items-center justify-between p-4 bg-background rounded-lg border border-border">
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center">
                    <Icon 
                      name={session?.device?.includes('iPhone') ? 'Smartphone' : 
                            session?.device?.includes('MacBook') ? 'Laptop' : 'Monitor'} 
                      size={20} 
                    />
                  </div>
                  <div>
                    <div className="flex items-center space-x-2">
                      <p className="font-medium text-foreground">{session?.device}</p>
                      {session?.current && (
                        <span className="px-2 py-1 text-xs font-medium bg-success text-success-foreground rounded-full">
                          Current
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">{session?.location} • {session?.ipAddress}</p>
                    <p className="text-xs text-muted-foreground">Last active: {session?.lastActive}</p>
                  </div>
                </div>
                {!session?.current && (
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="LogOut"
                    iconPosition="left"
                    onClick={() => handleLogoutSession(session?.id)}
                  >
                    Sign Out
                  </Button>
                )}
              </div>
            ))}
          </div>

          <div className="flex justify-end">
            <Button
              variant="destructive"
              iconName="LogOut"
              iconPosition="left"
            >
              Sign Out All Other Sessions
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SecuritySection;